"use client";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { EventRuntimeConfig } from "@/components/events/event-runtime";
import { ApiError } from "@/lib/api";
import { getOperationByKey, type CommandOptions } from "@/lib/api/suppliers";
import { createCommandKey } from "@/lib/commands/command-key";
import { eventAuthorityLost } from "@/lib/commands/event-feedback";
import {
  auctionStorageKey,
  readAuctionOperation,
  persistAuctionOperation,
  removeAuctionOperation,
  auctionCompletion,
  type AuctionIntent,
  type AuctionOperation,
} from "@/lib/commands/auction-operation";
import { templateFeedback, type TemplateFeedback } from "./template-runtime";
type Sender = (options: CommandOptions) => Promise<{ data: unknown }>;
interface Runtime {
  config: EventRuntimeConfig;
  record?: AuctionOperation;
  ready: boolean;
  available: boolean;
  pending: boolean;
  denied: boolean;
  generation: number;
  completedId?: string;
  feedback?: TemplateFeedback;
  deny: (error: unknown) => void;
  retry: () => void;
  restore: () => void;
  lookup: () => Promise<void>;
  run: (intent: AuctionIntent, send: Sender) => Promise<string | undefined>;
}
const Context = createContext<Runtime | null>(null);
export function AuctionRuntimeProvider({
  config,
  children,
}: {
  config: EventRuntimeConfig;
  children: ReactNode;
}) {
  const latest = useRef(config),
    active = useRef<AbortController | null>(null),
    mounted = useRef(false),
    denial = useRef(false);
  const [record, setRecord] = useState<AuctionOperation>(),
    [ready, setReady] = useState(false),
    [available, setAvailable] = useState(false),
    [pending, setPending] = useState(false),
    [denied, setDenied] = useState(false),
    [generation, setGeneration] = useState(0),
    [completedId, setCompletedId] = useState<string>(),
    [feedback, setFeedback] = useState<TemplateFeedback>();
  useEffect(() => {
    latest.current = config;
  }, [config]);
  function restore() {
    try {
      setRecord(readAuctionOperation(localStorage, latest.current.binding));
      setAvailable(true);
    } catch {
      setAvailable(false);
    }
    setReady(true);
  }
  useEffect(() => {
    mounted.current = true;
    const timer = setTimeout(restore, 0);
    const changed = (e: StorageEvent) => {
      if (!e.key || e.key === auctionStorageKey(latest.current.binding))
        restore();
    };
    window.addEventListener("storage", changed);
    return () => {
      mounted.current = false;
      active.current?.abort();
      clearTimeout(timer);
      window.removeEventListener("storage", changed);
    };
  }, []);
  function remove(row: AuctionOperation) {
    try {
      removeAuctionOperation(
        localStorage,
        latest.current.binding,
        row.idempotencyKey,
      );
      restore();
      return true;
    } catch {
      setAvailable(false);
      return false;
    }
  }
  function deny(error: unknown) {
    denial.current = true;
    active.current?.abort();
    setDenied(true);
    setCompletedId(undefined);
    try {
      const row = readAuctionOperation(localStorage, latest.current.binding);
      if (row) remove(row);
    } catch {
      setAvailable(false);
    }
    if (error instanceof ApiError && error.kind === "unauthorized")
      latest.current.invalidate(error);
  }
  function finish(row: AuctionOperation, data: unknown) {
    const id = auctionCompletion(data, row);
    if (!id) {
      setFeedback("unknown");
      return undefined;
    }
    if (!remove(row)) {
      setFeedback("storage");
      return undefined;
    }
    setCompletedId(id);
    setGeneration((x) => x + 1);
    setFeedback("success");
    return id;
  }
  const alive = (signal: AbortSignal) =>
    mounted.current && !signal.aborted && !denial.current;
  async function run(intent: AuctionIntent, send: Sender) {
    if (active.current || !ready || !available || record || denied)
      return undefined;
    if (!navigator.locks) {
      setFeedback("storage");
      return undefined;
    }
    const abort = new AbortController();
    active.current = abort;
    setPending(true);
    setFeedback(undefined);
    const row: AuctionOperation = {
      ...intent,
      idempotencyKey: createCommandKey("auction"),
      startedAt: Date.now(),
    };
    try {
      return await navigator.locks.request(
        auctionStorageKey(config.binding),
        { ifAvailable: true },
        async (lock) => {
          if (!lock || readAuctionOperation(localStorage, config.binding)) {
            restore();
            return undefined;
          }
          if (
            !(await latest.current.verify(abort.signal)) ||
            !alive(abort.signal)
          )
            return undefined;
          try {
            persistAuctionOperation(localStorage, config.binding, row);
            restore();
          } catch {
            setAvailable(false);
            setFeedback("storage");
            return undefined;
          }
          try {
            const { data } = await send({
              tenantId: config.binding.tenantId,
              csrfToken: latest.current.csrfToken,
              idempotencyKey: row.idempotencyKey,
              signal: abort.signal,
            });
            return alive(abort.signal) ? finish(row, data) : undefined;
          } catch (error) {
            if (!alive(abort.signal)) return undefined;
            const outcome = templateFeedback(error);
            setFeedback(outcome);
            if (outcome !== "unknown") remove(row);
            if (eventAuthorityLost(error)) deny(error);
            return undefined;
          }
        },
      );
    } catch (error) {
      if (alive(abort.signal)) {
        setFeedback(templateFeedback(error));
        if (eventAuthorityLost(error)) deny(error);
      }
    } finally {
      if (active.current === abort) active.current = null;
      if (mounted.current) setPending(false);
    }
  }
  async function lookup() {
    if (active.current || !record || denied) return;
    const abort = new AbortController();
    active.current = abort;
    setPending(true);
    setFeedback(undefined);
    try {
      if (!(await latest.current.verify(abort.signal)) || !alive(abort.signal))
        return;
      const { data } = await getOperationByKey(
        { tenantId: config.binding.tenantId, signal: abort.signal },
        record.operationType,
        record.idempotencyKey,
      );
      if (!alive(abort.signal)) return;
      if (data.state === "Completed") finish(record, data.result);
      else if (data.state === "Failed") {
        if (remove(record)) setFeedback("failed");
      } else setFeedback("unknown");
    } catch (error) {
      if (alive(abort.signal)) {
        setFeedback(templateFeedback(error));
        if (eventAuthorityLost(error)) deny(error);
      }
    } finally {
      if (active.current === abort) active.current = null;
      if (mounted.current) setPending(false);
    }
  }
  return (
    <Context.Provider
      value={{
        config,
        record,
        ready,
        available,
        pending,
        denied,
        generation,
        completedId,
        feedback,
        deny,
        run,
        lookup,
        restore,
        retry: () => {
          denial.current = false;
          setDenied(false);
          setGeneration((x) => x + 1);
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function useAuctionRuntime() {
  const context = useContext(Context);
  if (!context) throw new Error("Auction runtime unavailable");
  return context;
}
